/*    
<summary>
   This functional common component provides us custom file input box & has error handler that shows validation error. 
   Developer:Aashish Singh, Created Date:12-Nov-2024
</summary>
<returns>Returns JSX</returns>
*/
import React, { useState } from "react";
import { injectIntl } from "react-intl";
import FormikFormErrorHandler from "./FormikFormErrorHandler";
import { useField } from "formik";
import { formatMessage } from "../../translations/format-message";
import { toHexString } from "../../constants/common-constants";

interface IProps {
  children: React.ReactNode;
  className: string;
  errclassname: string | undefined;
  "data-testid"?: string;
  intl: any; //IntlConfig;
  name: string;
  onBlur: () => void;
  onChange: () => void;
  handleFileUploadHex: () => void;
  handleFileUploadAscii: (bytes:any) => void;
  showfilesizeerror: string;
  filesize: number;
  placeholder: string;
  value: string;
  disabled: boolean;
  selectedFileType: string;
}

const FormikFormFileSizeInputField = ({ handleFileUploadHex, handleFileUploadAscii, showfilesizeerror, selectedFileType, ...props }:IProps) => {
  const [field, meta, helpers] = useField(props);
  const errorClass = `${meta.error && meta.touched ? "error" : ""}`;
  const [fileError, setFileError] = useState<string>("");
  const _placeholder = props.placeholder
    ? props.intl.formatMessage({ id: props.placeholder })
    : props.placeholder;

  return (
    <>
      <input
        {...field}
        {...props}
        placeholder={_placeholder}
        className={props.className + " " + errorClass}
        disabled={props.disabled}
        type="file"
        onChange={async(e: React.ChangeEvent<HTMLInputElement>) => {
          const reader = new FileReader();
          let content: string = "";
          if (e.target.files != null && e.target.files.length > 0) {
            const file = e.target.files[0];
            helpers.setValue(e.target?.value); // setting file name
            if(selectedFileType == "0"){
              reader.onload = handleFileUploadHex;
              reader.readAsText(e?.target?.files[0]);
            }
            else{
              let buffer = await file.arrayBuffer();
              let bytes = new Uint8Array(buffer);
              content = toHexString(bytes);
              handleFileUploadAscii(content);
            }
            if(showfilesizeerror === "true" && !props.disabled){
              if(file && (selectedFileType == "0" ? file.size > props.filesize : content.length > props.filesize)){
                setFileError("file_limit_exceeded");
              }
              else
                setFileError("");
            }
          }
          else{
            if (showfilesizeerror === "true" && !props.disabled) setFileError("");
            helpers.setValue("");
          }
        }}
        onClick={(e: any) => {
          helpers.setValue("");
        }}
      />
      {
        meta.error && meta.touched &&
        <FormikFormErrorHandler intl={props.intl} meta={meta} />
      }
      {
        (showfilesizeerror === "true" && fileError !== "" && meta.touched && !meta.error) && !props.disabled &&
        <span data-testid="testError" className="text-danger errorMessage">{formatMessage(fileError)}</span>
      }
    </>
  );
};

export default injectIntl(FormikFormFileSizeInputField);
